import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLogin, useCompanySelect, useLogout } from './index';
import { useStoreActions } from 'stores/global';
import { useEvent } from 'lib/segment';
import type { Login, LoginContext } from '../types';

interface UseLoginWithNavigationOptions {
  onSuccessNavigateTo?: string;
  onNoTokenNavigateTo?: string;
  onCompanySelectSuccessNavigateTo?: string;
  onLogoutSuccessNavigateTo?: string;
  onCompanySelectSuccess?: () => void;
  onCompanySelectError?: (error: Error) => void;
}

export const useLoginWithNavigation = (options: UseLoginWithNavigationOptions = {}) => {
  const [searchParams] = useSearchParams();
  const event = useEvent();
  const returnUrl = searchParams.get('returnUrl') || '/';
  const companyReturnUrl =
    returnUrl === '/'
      ? '/switchcompany'
      : `/switchcompany?returnUrl=${encodeURIComponent(returnUrl)}`;
  const {
    onSuccessNavigateTo = returnUrl,
    onNoTokenNavigateTo = companyReturnUrl,
    onCompanySelectSuccessNavigateTo = returnUrl,
    onLogoutSuccessNavigateTo = '/',
    onCompanySelectSuccess,
    onCompanySelectError,
  } = options;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { resetPersistedState } = useStoreActions(state => state.urlState);

  const login = useLogin({
    onSuccess: (loginData: Login) => {
      queryClient.clear();
      if (loginData?.token) {
        navigate(onSuccessNavigateTo, { replace: true });
      } else {
        navigate(onNoTokenNavigateTo, { replace: true });
      }
    },
  });

  const companySelect = useCompanySelect({
    onSuccess: () => {
      queryClient.clear();
      navigate(onCompanySelectSuccessNavigateTo, { replace: true });
      resetPersistedState();
      onCompanySelectSuccess?.();
    },
    onError: onCompanySelectError,
  });

  const logout = useLogout({
    onSuccess: () => navigate(onLogoutSuccessNavigateTo, { replace: true }),
  });

  const submit = (user: string, password: string, RememberMe: boolean) => {
    login.login({ Username: user, Password: password, RememberMe });
  };

  const selectCompany = (loginContext: LoginContext, userData?: Login, length?: number) => {
    if (loginContext?.organizationType === 2) {
      event('Supplier Portal Login Context Selected', {
        objectType: 'Login Context',
        action: 'Selection',
        targetType: 'Organization Context',
        targetId: loginContext.organizationId,
        targetName: loginContext.organizationName,
        source: 'Login Selection',
        page: 'Multi-Select Login',
        businessContext: {
          userId: loginContext.id,
          loginContextType: 'supplier_portal',
          multipleContextsAvailable: length ? length > 1 : false,
        },
      });
    }
    // Return the promise so callers can await completion (e.g. to keep a switch trigger
    // disabled until the request settles). The rejection is swallowed here so callers stay
    // fire-and-forget — failures are surfaced via the mutation's `onError` (onCompanySelectError),
    // not by throwing at the call site (matching the previous `.mutate` behaviour).
    return companySelect
      .mutateAsync({
        loginContext,
        Username: userData?.Username,
        Password: userData?.Password,
        user: userData?.user,
        RememberMe: userData?.RememberMe || false,
      })
      .catch(() => {
        /* surfaced via onCompanySelectError */
      });
  };

  const handleLogout = () => {
    logout();
  };

  return {
    login,
    companySelect,
    logout,
    submit,
    selectCompany,
    handleLogout,
  };
};
